﻿import { formatDate, checkDateValidity } from '../../../../Foundation/utils';
import { evaluateWarrantyDate, WARRANTY_MODIFIER } from '../utils/evaluateWarrantyDates';
import {
  isExpiredForFlow,
  hasValidFlowDate,
  isEarmouldDisabledForFlow,
  isLossAndDamageUsed,
} from '../utils/flow-expiry';

const RELATED_ITEMS_PAGE_SIZE = 500;

export class RelatedItemsSidebar {
  constructor(element) {
    this.el = element;

    this._refs = {
      closeBtn: element.querySelector('[data-sidebar-close]'),
      cancelBtn: element.querySelector('[data-sidebar-cancel]'),
      addBtn: element.querySelector('[data-sidebar-add]'),
      orderNo: element.querySelector('[data-sidebar-order-no]'),
      error: element.querySelector('[data-sidebar-error]'),
      searchInput: element.querySelector('[data-sidebar-search]'),
      searchWrapper: element.querySelector('[data-sidebar-search-wrapper]'),
      dropdown: element.querySelector('[data-sidebar-dropdown]'),
      dropdownList: element.querySelector('[data-sidebar-dropdown-list]'),
      selectAllBtn: element.querySelector('[data-sidebar-select-all]'),
      noResults: element.querySelector('[data-sidebar-no-results]'),
      noResultsTitle: element.querySelector('[data-sidebar-no-results-title]'),
    };

    this._tableInstance = null;
    this._serialNo = null;
    this._allItems = [];
    this._selectedItems = new Map();
    this._tableConfig = this._parseTableConfig();

    this._addEventListeners();
  }

  _addEventListeners() {
    document.addEventListener('related-items:open', (e) => {
      this._tableInstance = e.detail?.tableInstance || null;
      this._serialNo = e.detail?.serialNo || null;
      this._allItems = [];
      const alreadySelected = this._getAlreadyInTableSerials();
      this._selectedItems = new Map();
      this._clearError();
      this._clearSearch();
      this._open();
      this._fetchItems(alreadySelected);
    });

    if (this._refs.closeBtn) {
      this._refs.closeBtn.addEventListener('click', () => this._close());
    }

    if (this._refs.cancelBtn) {
      this._refs.cancelBtn.addEventListener('click', () => this._close());
    }

    if (this._refs.addBtn) {
      this._refs.addBtn.addEventListener('click', () => this._addToList());
    }

    if (this._refs.selectAllBtn) {
      this._refs.selectAllBtn.addEventListener('click', () => this._selectAll());
    }

    if (this._refs.searchInput) {
      this._refs.searchInput.addEventListener('input', () => {
        this._renderDropdownItems();
        this._updateSelectAllVisibility();
        this._updateSelectAllLabel();
      });
    }

    this.el.addEventListener('click', (e) => {
      if (e.target === this.el || e.target.classList.contains('related-items-sidebar__overlay')) {
        this._close();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._isOpen()) {
        this._close();
      }
    });
  }

  _parseTableConfig() {
    try {
      const configData = this.el.getAttribute('data-related-items-table-config');
      if (!configData || configData.trim() === '') return {};
      return JSON.parse(configData);
    } catch (e) {
      console.error('RelatedItemsSidebar: table config is not valid JSON!', e);
      return {};
    }
  }

  _getTableConfig() {
    return this._tableInstance?._config || this._tableConfig || {};
  }

  _getSlideInTranslations() {
    const tableConfig = this._getTableConfig();
    const translations = tableConfig.Translations || {};

    return {
      ...translations.slideInTranslations,
      returnedLabel: translations.returnedLabel,
      expiredLabel: translations.expiredLabel,
      usedLabel: translations.usedLabel,
    };
  }

  _isOpen() {
    return this.el.getAttribute('aria-hidden') === 'false';
  }

  _open() {
    // Compensate for scrollbar disappearing when overflow:hidden is applied — preventing layout shift.
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    this.el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sidebar-open');

    if (this._refs.closeBtn) {
      this._refs.closeBtn.focus();
    }
  }

  _close() {
    this.el.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sidebar-open');
    document.body.style.paddingRight = '';
  }

  _clearError() {
    if (this._refs.error) {
      this._refs.error.setAttribute('aria-hidden', 'true');
    }
  }

  _showError() {
    if (this._refs.error) {
      this._refs.error.setAttribute('aria-hidden', 'false');
    }
  }

  _clearSearch() {
    if (this._refs.searchInput) {
      this._refs.searchInput.value = '';
    }
  }

  async _fetchItems(alreadySelected = new Set()) {
    if (!this._serialNo || !this._tableInstance) return;

    const config = this._tableInstance._config;
    const endpoint = config.RelatedSerialNumberEndpoint;

    if (!endpoint) {
      console.warn('[RelatedItemsSidebar] RelatedSerialNumberEndpoint not configured');
      return;
    }

    if (this._refs.orderNo) {
      this._refs.orderNo.textContent = this._serialNo;
    }

    try {
      this._allItems = await this._fetchAllItems({ endpoint, config });

      const hasItems = this._allItems.length;

      if (hasItems) {
        // Pre-select items already checked in main table
        this._allItems.forEach((item) => {
          if (alreadySelected.has(String(item.SerialNumber || ''))) {
            this._selectedItems.set(String(item.SerialNumber || ''), item);
          }
        });

        this._renderDropdownItems();
        this._updateSelectAllVisibility();
        this._updateSelectAllLabel();
      }
    } catch (error) {
      console.error('[RelatedItemsSidebar] Failed to fetch related items:', error);
    }
  }

  async _fetchAllItems({ endpoint, config }) {
    const firstPage = await this._fetchItemsPage({ endpoint, config, skip: 0 });
    const allItems = firstPage.RelatedSerialNumbersWarranties || [];
    const totalCount = Number(firstPage.TotalCount) || allItems.length;

    if (allItems.length >= totalCount) return allItems;

    const remainingCount = totalCount - allItems.length;
    const remainingPage = await this._fetchItemsPage({
      endpoint,
      config,
      skip: allItems.length,
      take: remainingCount,
    });
    allItems.push(...(remainingPage.RelatedSerialNumbersWarranties || []));

    return allItems;
  }

  async _fetchItemsPage({ endpoint, config, skip, take = RELATED_ITEMS_PAGE_SIZE }) {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        UserId: config.UserId,
        APIKey: config.ApiKey,
        SerialNumber: this._serialNo,
        SearchWord: '',
        Skip: skip,
        Take: take,
      }),
    });

    if (!response.ok) {
      throw new Error(`Server error. HTTP status: ${response.status}`);
    }

    return response.json();
  }

  _getFilteredItems() {
    const query = this._refs.searchInput?.value.trim().toLowerCase() || '';
    if (!query) return this._allItems;
    return this._allItems.filter(
      (item) =>
        (item.SerialNumber || '').toLowerCase().includes(query) ||
        (item.Patient || '').toLowerCase().includes(query),
    );
  }

  _getAlreadyInTableSerials() {
    if (!this._tableInstance) return new Set();
    return new Set(this._tableInstance._state.selectedItemsData.keys());
  }

  _getSelectableItems(items = this._getFilteredItems()) {
    const alreadyInTable = this._getAlreadyInTableSerials();

    return items.filter(
      (item) => !this._isItemBlocked(item) && !alreadyInTable.has(String(item.SerialNumber || '')),
    );
  }

  _isItemBlocked(item) {
    if (item.Returned) return true;

    const warrantyKey = this._tableInstance?._warrantyKey;

    if (isLossAndDamageUsed(warrantyKey, item)) return true;

    if (warrantyKey && !checkDateValidity(item[warrantyKey])) {
      return true;
    }

    const config = this._tableInstance?._config;
    if (isEarmouldDisabledForFlow(config?.FlowType, item, config)) {
      return true;
    }

    return isExpiredForFlow(config?.FlowType, item);
  }

  _shouldShowExpiredBadge(item) {
    const flowType = this._tableInstance?._config?.FlowType;
    const warrantyKey = this._tableInstance?._warrantyKey;

    if (warrantyKey && !checkDateValidity(item[warrantyKey])) {
      return false;
    }

    const config = this._tableInstance?._config;
    if (isEarmouldDisabledForFlow(flowType, item, config)) {
      return true;
    }

    return hasValidFlowDate(flowType, item) && isExpiredForFlow(flowType, item);
  }

  _renderDropdownItems() {
    const { dropdownList } = this._refs;
    if (!dropdownList) return;

    dropdownList.innerHTML = '';
    const items = this._getFilteredItems();
    const alreadyInTable = this._getAlreadyInTableSerials();

    items.forEach((item) => {
      dropdownList.appendChild(this._createDropdownItem(item, alreadyInTable));
    });

    if (items.length === 0) {
      if (this._refs.dropdown) this._refs.dropdown.setAttribute('aria-hidden', 'true');
      if (this._refs.noResults) {
        const query = this._refs.searchInput?.value.trim() || '';

        if (query) {
          const tableConfig = this._getTableConfig();
          const noResultsLabel =
            tableConfig.Translations?.noResultsLabel || tableConfig.translations?.noResultsLabel; //fallback for static FE

          if (this._refs.noResultsTitle) {
            this._refs.noResultsTitle.textContent = `${noResultsLabel} "${query}"`;
          }

          this._refs.noResults.setAttribute('aria-hidden', 'false');
        } else {
          this._refs.noResults.setAttribute('aria-hidden', 'true');
        }
      }
    } else {
      if (this._refs.dropdown) this._refs.dropdown.setAttribute('aria-hidden', 'false');
      if (this._refs.noResults) {
        this._refs.noResults.setAttribute('aria-hidden', 'true');
      }
    }
  }

  _updateSelectAllVisibility() {
    if (!this._refs.selectAllBtn) return;
    const selectableItems = this._getSelectableItems();
    this._refs.selectAllBtn.setAttribute('aria-hidden', String(selectableItems.length < 2));
  }

  _selectAll() {
    const selectableItems = this._getSelectableItems();
    const allSelected = selectableItems.every((item) =>
      this._selectedItems.has(String(item.SerialNumber || '')),
    );

    if (allSelected) {
      selectableItems.forEach((item) =>
        this._selectedItems.delete(String(item.SerialNumber || '')),
      );
    } else {
      selectableItems.forEach((item) =>
        this._selectedItems.set(String(item.SerialNumber || ''), item),
      );
    }

    this._updateSelectAllLabel();
    this._clearError();
    this._renderDropdownItems();
  }

  _updateSelectAllLabel() {
    if (!this._refs.selectAllBtn) return;
    const selectableItems = this._getSelectableItems();
    const allSelected =
      selectableItems.length > 0 &&
      selectableItems.every((item) => this._selectedItems.has(String(item.SerialNumber || '')));
    const translations = this._getSlideInTranslations();
    const selectLabel = translations.selectAllLabel;
    const deselectLabel = translations.deselectAllLabel;
    this._refs.selectAllBtn.textContent = allSelected ? deselectLabel : selectLabel;
  }

  _createDropdownItem(item, alreadyInTable) {
    const serialNo = String(item.SerialNumber || '');
    const isReturned = !!item.Returned;
    const isUsed = isLossAndDamageUsed(this._tableInstance?._warrantyKey, item);
    const isExpiredBlocked = !isReturned && !isUsed && this._isItemBlocked(item);
    const isAlreadyInTable = alreadyInTable.has(serialNo);
    const isSelected = this._selectedItems.has(serialNo);

    const li = document.createElement('li');
    li.className = 'related-items-sidebar__dropdown-item';

    if (isReturned || isUsed || isExpiredBlocked || isAlreadyInTable) {
      li.classList.add('related-items-sidebar__dropdown-item--disabled');
    }

    const imageEl = document.createElement('div');
    imageEl.className = 'related-items-sidebar__card-image';

    const body = document.createElement('div');
    body.className = 'related-items-sidebar__card-body';

    const title = document.createElement('p');
    title.className = 'related-items-sidebar__card-title';
    title.textContent = item.Description || '-';
    body.appendChild(title);

    const translations = this._getSlideInTranslations();
    const serialNoLabel = translations.serialNoLabel;
    const patientLabel = translations.patientNameLabel;
    const warrantyKey = this._tableInstance?._warrantyKey;
    const columns = this._getTableConfig().Columns || this._getTableConfig().columns || [];
    const warrantyColumn =
      columns.find((c) => c.key === warrantyKey) || columns.find((c) => c.type === 'warranty');
    const warrantyLabel = warrantyColumn?.label || '';

    body.appendChild(this._createCardRow(serialNoLabel, serialNo));
    body.appendChild(this._createCardRow(patientLabel, item.Patient || '-'));

    if (warrantyLabel && warrantyKey) {
      body.appendChild(
        this._createWarrantyRow(warrantyLabel, item[warrantyKey], isReturned, isUsed),
      );
    } else {
      console.error('[RelatedItemsSidebar] Warranty row cannot be rendered due to missing data.');
    }

    li.appendChild(imageEl);
    li.appendChild(body);

    if (isReturned) {
      const badge = document.createElement('span');
      badge.className =
        'related-items-sidebar__card-badge related-items-sidebar__card-badge--returned';
      badge.textContent = translations.returnedLabel;
      li.appendChild(badge);
    } else if (isUsed) {
      const badge = document.createElement('span');
      badge.className = 'related-items-sidebar__card-badge related-items-sidebar__card-badge--used';
      badge.textContent = translations.usedLabel;
      li.appendChild(badge);
    } else if (isExpiredBlocked) {
      if (this._shouldShowExpiredBadge(item)) {
        const badge = document.createElement('span');
        badge.className =
          'related-items-sidebar__card-badge related-items-sidebar__card-badge--expired';
        badge.textContent = translations.expiredLabel;
        li.appendChild(badge);
      }
    } else if (isAlreadyInTable) {
      const badge = document.createElement('span');
      badge.className =
        'related-items-sidebar__card-badge related-items-sidebar__card-badge--in-card';
      badge.textContent = translations.alreadyInTheCartLabel || translations.alreadyInCartLabel; //fallback for static FE
      li.appendChild(badge);
    } else {
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'related-items-sidebar__card-checkbox';
      checkbox.value = serialNo;
      checkbox.checked = isSelected;
      // Keep native checkbox for existing styling/state, but remove it from keyboard and screen reader flow.
      checkbox.tabIndex = -1;
      checkbox.setAttribute('aria-hidden', 'true');

      li.setAttribute('role', 'checkbox');
      li.setAttribute('tabindex', '0');
      li.setAttribute('aria-checked', String(isSelected));
      li.setAttribute('aria-label', `${item.Description || '-'}, ${serialNoLabel} ${serialNo}`);

      const updateSelection = (selected) => {
        checkbox.checked = selected;
        li.setAttribute('aria-checked', String(selected));

        if (selected) {
          this._selectedItems.set(serialNo, item);
        } else {
          this._selectedItems.delete(serialNo);
        }

        this._clearError();
        this._updateSelectAllLabel();
      };

      const toggleSelection = () => updateSelection(!checkbox.checked);

      checkbox.addEventListener('change', () => {
        updateSelection(checkbox.checked);
      });

      li.addEventListener('click', (e) => {
        if (e.target === checkbox) return;
        toggleSelection();
      });

      li.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        toggleSelection();
      });

      li.appendChild(checkbox);
    }

    return li;
  }

  _createCardRow(label, value) {
    const row = document.createElement('div');
    row.className = 'related-items-sidebar__card-row';

    const labelEl = document.createElement('span');
    labelEl.className = 'related-items-sidebar__card-label';
    labelEl.textContent = label;

    const valueEl = document.createElement('span');
    valueEl.className = 'related-items-sidebar__card-value';
    valueEl.textContent = value;

    row.appendChild(labelEl);
    row.appendChild(valueEl);

    return row;
  }

  _createWarrantyRow(label, rawDate, isReturned, isUsed = false) {
    const row = this._createCardRow(label, '');
    const valueEl = row.querySelector('.related-items-sidebar__card-value');
    const { modifier, date } = evaluateWarrantyDate(rawDate, isReturned, {}, isUsed);

    if (!date) {
      valueEl.textContent = '-';
      return row;
    }

    valueEl.textContent = formatDate(date, this._getTableConfig().DateFormat || 'dd-mm-yyyy');
    valueEl.classList.add('related-items-sidebar__card-value--warranty');

    if (modifier === WARRANTY_MODIFIER.EXPIRED) {
      valueEl.classList.add('related-items-sidebar__card-value--expired');
    } else if (modifier === WARRANTY_MODIFIER.VALID) {
      valueEl.classList.add('related-items-sidebar__card-value--valid');
    }

    return row;
  }

  _addToList() {
    if (this._selectedItems.size === 0) {
      this._showError();
      return;
    }

    this._clearError();

    if (this._tableInstance) {
      this._selectedItems.forEach((item, serialNo) => {
        if (this._tableInstance.setSelectedItemData) {
          this._tableInstance.setSelectedItemData(serialNo, item);
        }

        const checkboxes = this._tableInstance._refs.tbody.querySelectorAll(
          `.service-results-table__checkbox[value="${CSS.escape(
            serialNo,
          )}"]:not(.service-results-table__checkbox--mobile)`,
        );
        if (checkboxes.length > 0) {
          checkboxes.forEach((cb) => {
            cb.checked = true;
            const row = cb.closest('tr');
            if (row) {
              row.querySelectorAll('.service-results-table__checkbox').forEach((c) => {
                c.checked = true;
              });
            }
          });
        }
      });

      this._tableInstance._updateNextStepBar();
    }

    this._close();
  }
}
